import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Hammer, LogOut, Menu, X } from "lucide-react";
import AuthenticityTokenField from "../forms/AuthenticityTokenField";
import { Button } from "../ui/button";
import { AppLink } from "../ui/link";
import { HeaderAuthLinks } from "./HeaderAuthLinks";
import { DashboardNavLinks } from "./DashboardNavLinks";
import { AdminNavLinks } from "./AdminNavLinks";
import { mobileScrollLinkClass, scrollLinkClass } from "./navLinkStyles";
import type { RouteMap, UserDisplay } from "../../pages/MainAppPage/types";
import type { AdminRouteMap } from "../../pages/AdminAppPage/types";
import { useCurrentPath } from "../../hooks";

type AppHeaderVariant = "landing" | "app";

type AppHeaderProps = {
  variant: AppHeaderVariant;
  loggedIn: boolean;
  hasBusiness: boolean;
  hasOrganization?: boolean;
  hasJobSitePhotos?: boolean;
  hasCredentials?: boolean;
  canAccessBackend?: boolean;
  routes: RouteMap;
  csrfToken: string;
  page?: string;
  unreadQuoteCount?: number;
  incomingOrgInvitationCount?: number;
  userDisplay?: UserDisplay | null;
  scrollToSection?: (id: string) => void;
  admin?: boolean;
  adminPath?: string;
  adminRoutes?: AdminRouteMap;
  isAdminContext?: boolean;
};

export const AppHeader: React.FC<AppHeaderProps> = ({
  variant,
  loggedIn,
  hasBusiness,
  hasOrganization,
  hasJobSitePhotos,
  hasCredentials,
  canAccessBackend,
  routes,
  csrfToken,
  page,
  unreadQuoteCount = 0,
  incomingOrgInvitationCount = 0,
  userDisplay,
  scrollToSection,
  admin,
  adminPath,
  adminRoutes,
  isAdminContext,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const logoutFormRef = React.useRef<HTMLFormElement>(null);
  const currentPath = useCurrentPath();
  const isLanding = variant === "landing";
  const isDashboardContext =
    page === "dashboard" ||
    page === "onboarding" ||
    page === "quotes" ||
    page === "seo" ||
    page === "features" ||
    page === "account" ||
    page === "settings" ||
    page === "billing" ||
    page === "org_members" ||
    page === "jobs" ||
    page === "job_new" ||
    page === "job_show" ||
    page === "credentials" ||
    page === "credential_new" ||
    page === "credential_show" ||
    page === "credentials_roster" ||
    page === "teammate_home";
  const showAdminLink = admin && adminPath;

  const handleScroll = (id: string) => {
    setMobileMenuOpen(false);
    scrollToSection?.(id);
  };

  const handleSignOutClick = () => {
    logoutFormRef.current?.requestSubmit();
  };

  const LogoIcon = Hammer;
  const logoContent = (
    <>
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-linear-to-br from-premium-500 to-premium-600 shadow-lg shadow-premium-500/20 transition-transform group-hover:scale-105 md:h-10 md:w-10">
        <LogoIcon className="h-5 w-5 text-primary-foreground md:h-6 md:w-6" />
      </div>
      <span className="font-display text-lg font-bold tracking-tight text-foreground md:text-2xl">
        Tool<span className="text-premium-600">Kit</span>
      </span>
    </>
  );

  const scrollLinks = isLanding && scrollToSection && (
    <>
      <Button
        type="button"
        variant="ghost"
        onClick={() => handleScroll("features")}
        className={scrollLinkClass}
      >
        Features
      </Button>
      <Button
        type="button"
        variant="ghost"
        onClick={() => handleScroll("products")}
        className={scrollLinkClass}
      >
        Products
      </Button>
      <Button
        type="button"
        variant="ghost"
        onClick={() => handleScroll("trades")}
        className={scrollLinkClass}
      >
        Trades
      </Button>
      <Button
        type="button"
        variant="ghost"
        onClick={() => handleScroll("why-toolkit")}
        className={scrollLinkClass}
      >
        Why ToolKit?
      </Button>
      <Button
        type="button"
        variant="ghost"
        onClick={() => handleScroll("pricing")}
        className={scrollLinkClass}
      >
        Pricing
      </Button>
    </>
  );

  return (
    <nav className="fixed w-full z-50 bg-card/95 backdrop-blur-sm border-b border-border shadow-sm">
      <form
        ref={logoutFormRef}
        action={routes.logout}
        method="post"
        className="sr-only"
      >
        <AuthenticityTokenField csrfToken={csrfToken} />
        <input type="hidden" name="_method" value="delete" />
      </form>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-[var(--ds-app-header-height)] items-center justify-between">
          {isLanding ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleScroll("home")}
              className="group flex items-center gap-2.5 h-auto p-0 hover:bg-transparent"
            >
              {logoContent}
            </Button>
          ) : (
            <AppLink
              to={routes.root}
              className="group flex items-center gap-2.5 hover:no-underline"
            >
              {logoContent}
            </AppLink>
          )}

          <div className="hidden md:flex items-center gap-4 md:space-x-8 md:gap-0">
            {scrollLinks}
            {scrollLinks && <div className="h-6 w-px bg-border mx-2" />}
            {showAdminLink && (
              <AppLink
                to={adminPath}
                className="font-medium text-muted-foreground hover:text-foreground transition-colors no-underline hover:no-underline"
              >
                Admin
              </AppLink>
            )}
            <HeaderAuthLinks
              layout="desktop"
              loggedIn={loggedIn}
              hasBusiness={hasBusiness}
              hasOrganization={hasOrganization}
              canAccessBackend={canAccessBackend}
              routes={routes}
              userDisplay={userDisplay}
              onSignOut={handleSignOutClick}
            />
          </div>

          <div className="md:hidden flex items-center">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-muted-foreground hover:text-foreground relative w-7 h-7"
            >
              <AnimatePresence initial={false}>
                {mobileMenuOpen ? (
                  <motion.span
                    key="close"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className="absolute"
                  >
                    <X className="w-7 h-7" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="menu"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className="absolute"
                  >
                    <Menu className="w-7 h-7" />
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="md:hidden bg-card border-b border-border overflow-hidden"
          >
            <div className="px-4 pt-2 pb-6 space-y-3 shadow-lg">
              {isLanding && scrollToSection && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleScroll("features")}
                    className={mobileScrollLinkClass}
                  >
                    Features
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleScroll("products")}
                    className={mobileScrollLinkClass}
                  >
                    Products
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleScroll("trades")}
                    className={mobileScrollLinkClass}
                  >
                    Trades
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleScroll("why-toolkit")}
                    className={mobileScrollLinkClass}
                  >
                    Why ToolKit?
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleScroll("pricing")}
                    className={mobileScrollLinkClass}
                  >
                    Pricing
                  </Button>
                  <div className="h-px w-full bg-border my-2" />
                </>
              )}
              {isAdminContext && loggedIn && adminRoutes && (
                <>
                  <AdminNavLinks
                    routes={adminRoutes}
                    currentPath={currentPath}
                    variant="mobileMenu"
                    onNavigate={() => setMobileMenuOpen(false)}
                  />
                  <div className="h-px w-full bg-border my-1.5" />
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleSignOutClick()}
                    className={`${mobileScrollLinkClass} flex items-center gap-3 text-destructive hover:text-destructive`}
                  >
                    <LogOut className="w-4 h-4 shrink-0" />
                    Sign out
                  </Button>
                </>
              )}
              {isDashboardContext && loggedIn && !isAdminContext && (
                <>
                  <DashboardNavLinks
                    routes={routes}
                    adminRoutes={adminRoutes}
                    admin={admin}
                    canAccessBackend={canAccessBackend}
                    currentPath={currentPath}
                    unreadQuoteCount={unreadQuoteCount}
                    incomingOrgInvitationCount={incomingOrgInvitationCount}
                    hasWebsite={hasBusiness}
                    hasJobSitePhotos={hasJobSitePhotos}
                    hasCredentials={hasCredentials}
                    variant="mobileMenu"
                    onNavigate={() => setMobileMenuOpen(false)}
                  />
                  <div className="h-px w-full bg-border my-1.5" />
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleSignOutClick()}
                    className={`${mobileScrollLinkClass} flex items-center gap-3 text-destructive hover:text-destructive`}
                  >
                    <LogOut className="w-4 h-4 shrink-0" />
                    Sign out
                  </Button>
                </>
              )}
              {!isDashboardContext && !isAdminContext && (
                <HeaderAuthLinks
                  layout="mobile"
                  loggedIn={loggedIn}
                  hasBusiness={hasBusiness}
                  hasOrganization={hasOrganization}
                  canAccessBackend={canAccessBackend}
                  routes={routes}
                  userDisplay={userDisplay}
                  onSignOut={handleSignOutClick}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
